/**
 * 裁剪工具（M3-11 / DESIGN.md §6.1，C 键）。
 *
 * Photoshop 的手感：
 * - 从别的工具切过来，立刻出现一个盖满画布的裁剪框（不用先拖一下才有框）
 * - 有框时：框有 8 个手柄可以拖拽调整大小，框内可以拖拽整体挪动——手柄
 *   数学直接复用自由变换那一套（`engine/transform/handles.ts`），都是
 *   "矩形 + 8 个归一化位置的手柄"，没有理由另写一份
 * - Enter 提交 / Esc 取消之后框会隐藏，且**不会**自动再给一个满画布框——
 *   此时拖拽是从头画一个新框（跟经典选框工具一样，从按下点拖到松开点）；
 *   只有真正切到别的工具再切回来，才会重新看到满画布默认框
 *
 * 比例约束（工具选项栏的"比例"下拉）：非自由模式下，调整大小时把新尺寸
 * 按当前手柄反推、锁定宽高比——角手柄两轴都按比例算，边手柄只锁另一轴。
 * "原始比例"取文档当前宽高比，其余是预设数值比例。框还不存在、从头拖出
 * 一个新框时不受比例约束——那只是给个大致范围，用手柄精调时才需要锁比例。
 *
 * 提交/取消跟旧版一致：Enter 走 cropDocument（一条历史），Esc 丢弃会话
 * （不留历史）——这两者都不属于这个文件要变的范围。
 */
import { handlePoint, hitTestHandles, type ScaleHandle } from '../../engine/transform/handles';
import { cropDocument } from '../../app/crop';
import type { AppStore } from '../../app/store';
import type { Rect } from '../../document/types';

export type CropAspect = 'free' | 'original' | '1:1' | '4:3' | '3:2' | '16:9';

const ASPECT_RATIOS: Readonly<Record<Exclude<CropAspect, 'free' | 'original'>, number>> = {
  '1:1': 1,
  '4:3': 4 / 3,
  '3:2': 3 / 2,
  '16:9': 16 / 9,
};

export interface CropToolDeps {
  readonly store: AppStore;
  readonly tools: { readonly active: { readonly id: string }; readonly getOption: (toolId: string, key: string) => unknown };
  readonly requestRender: () => void;
}

type DragKind = ScaleHandle | 'move';

export class CropToolController {
  #deps: CropToolDeps;
  #rect: Rect | null = null;
  #wasActive = false;
  #drag: { kind: DragKind; start: { x: number; y: number }; startRect: Rect } | null = null;
  /** 没有框时，从按下点拖到当前点画一个新框（提交/取消之后的状态）。 */
  #drawStart: { x: number; y: number } | null = null;

  constructor(deps: CropToolDeps) {
    this.#deps = deps;
  }

  /**
   * 当前裁剪框（文档空间）。null = 没有会话。
   *
   * 读之前先兜底一次 `#ensureDefaultRect`——不然"切工具立刻有满画布默认
   * 框"这件事就只对画布覆盖层成立（它每帧都会调 `drawOverlay`），工具
   * 属性栏的宽/高栏（main.tsx 直接读这个 getter，不走 handlePointer/
   * drawOverlay 那几条路）在画布下一帧重绘之前读到的会是还没初始化的
   * null，跟画布上看到的框对不上。
   */
  get rect(): Rect | null {
    this.#ensureDefaultRect();
    return this.#rect;
  }

  get active(): boolean {
    this.#ensureDefaultRect();
    return this.#rect !== null;
  }

  #aspectRatio(): number | null {
    const value = this.#deps.tools.getOption('crop', 'aspect') as CropAspect;
    if (value === 'free') return null;
    if (value === 'original') {
      const doc = this.#deps.store.document;
      return doc === null ? null : doc.size.width / doc.size.height;
    }
    return ASPECT_RATIOS[value];
  }

  /** 切到裁剪工具时若还没有框，盖满整个画布——不用先拖一下才有东西可调。 */
  #ensureDefaultRect(): void {
    const isCropActive = this.#deps.tools.active.id === 'crop';
    if (!isCropActive) {
      this.#wasActive = false;
      return;
    }
    if (this.#wasActive) return;
    this.#wasActive = true;
    const doc = this.#deps.store.document;
    if (doc === null || this.#rect !== null) return;
    this.#rect = { x: 0, y: 0, width: doc.size.width, height: doc.size.height };
  }

  /** 指针处理。tolerance 是文档像素（调用方按视口缩放换算）。返回 true 表示已消费。 */
  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    point: { x: number; y: number },
    tolerance: number,
  ): boolean {
    this.#ensureDefaultRect();
    if (this.#deps.tools.active.id !== 'crop') return false;
    const doc = this.#deps.store.document;
    if (doc === null) return true;

    // 正在从头画一个新框（down 那次判定 #rect 是 null 才置位 #drawStart）：
    // 用 #drawStart 是否非空来判断这次拖拽是不是"画新框"进行中，而不是
    // 看 #rect 是否非空——第一次 move 就会把 #rect 填上（要能实时预览
    // 边框跟着拖），如果改用 #rect === null 判断，从第二次 move 起就会
    // 误判成"已经有框了"，掉进下面手柄逻辑分支、拖拽再也不生效。
    if (this.#drawStart !== null) {
      const clamp = (p: { x: number; y: number }): { x: number; y: number } => ({
        x: Math.min(doc.size.width, Math.max(0, Math.round(p.x))),
        y: Math.min(doc.size.height, Math.max(0, Math.round(p.y))),
      });
      const a = this.#drawStart;
      const b = clamp(point);
      const drawn = {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        width: Math.abs(b.x - a.x),
        height: Math.abs(b.y - a.y),
      };
      this.#rect = drawn;
      if (phase === 'up') {
        this.#drawStart = null;
        // 退化的框（点一下没拖）不留会话
        if (drawn.width < 2 || drawn.height < 2) this.#rect = null;
      }
      this.#deps.requestRender();
      return true;
    }

    // 没有框、也没在画（Enter/Esc 之后的状态）：按下就是开始从头画一个
    // 新框，跟经典选框工具一样，不是在一个不存在的框上找手柄
    if (this.#rect === null) {
      if (phase === 'down') {
        this.#drawStart = {
          x: Math.min(doc.size.width, Math.max(0, Math.round(point.x))),
          y: Math.min(doc.size.height, Math.max(0, Math.round(point.y))),
        };
      }
      return true;
    }

    if (phase === 'down') {
      const handle = hitTestHandles(this.#rect, point, tolerance);
      const inside =
        point.x >= this.#rect.x &&
        point.x <= this.#rect.x + this.#rect.width &&
        point.y >= this.#rect.y &&
        point.y <= this.#rect.y + this.#rect.height;
      const kind: DragKind | null = handle !== null && handle !== 'rotate' ? handle : inside ? 'move' : null;
      if (kind === null) return true; // 框外空处：吞掉但不动
      this.#drag = { kind, start: point, startRect: this.#rect };
      return true;
    }
    if ((phase === 'move' || phase === 'up') && this.#drag !== null) {
      const dx = point.x - this.#drag.start.x;
      const dy = point.y - this.#drag.start.y;
      this.#rect = clampToCanvas(
        this.#drag.kind === 'move'
          ? moveRect(this.#drag.startRect, dx, dy)
          : resizeRect(this.#drag.startRect, this.#drag.kind, dx, dy, this.#aspectRatio()),
        doc.size.width,
        doc.size.height,
      );
      if (phase === 'up') this.#drag = null;
      this.#deps.requestRender();
      return true;
    }
    return true; // 裁剪工具激活期间独占指针
  }

  /**
   * 工具选项栏切换「比例」下拉时调用：选框立刻按新比例改形，而不是等下一次
   * 拖手柄才生效——取当前框内能放下的最大新比例矩形，居中对齐（PS 的手感）。
   */
  applyAspectOption(): void {
    this.#ensureDefaultRect();
    if (this.#deps.tools.active.id !== 'crop' || this.#rect === null) return;
    const aspect = this.#aspectRatio();
    if (aspect === null) return;
    this.#rect = fitAspectWithin(this.#rect, aspect);
    this.#deps.requestRender();
  }

  /**
   * 工具选项栏直接敲「宽度」/「高度」时调用（跟真实 Photoshop 裁剪属性栏
   * 一致，比例下拉之外也能手动输入数字）——以框当前中心为锚点改这一个
   * 维度；锁了比例的话另一维度跟着联动，不锁就只改这一维度、对边不动。
   * 没有框（比如刚提交完还没拖出新框）时什么也不做——没有框可以改。
   */
  applySizeOption(dimension: 'width' | 'height', value: number): void {
    this.#ensureDefaultRect();
    if (this.#deps.tools.active.id !== 'crop' || this.#rect === null) return;
    const doc = this.#deps.store.document;
    if (doc === null) return;
    const next = Math.max(1, Math.round(value));
    const aspect = this.#aspectRatio();
    let width = dimension === 'width' ? next : this.#rect.width;
    let height = dimension === 'height' ? next : this.#rect.height;
    if (aspect !== null && aspect > 0) {
      if (dimension === 'width') height = width / aspect;
      else width = height * aspect;
    }
    const cx = this.#rect.x + this.#rect.width / 2;
    const cy = this.#rect.y + this.#rect.height / 2;
    this.#rect = clampToCanvas(
      { x: cx - width / 2, y: cy - height / 2, width, height },
      doc.size.width,
      doc.size.height,
    );
    this.#deps.requestRender();
  }

  /**
   * Enter：提交裁剪并让选框消失。返回是否发生了裁剪。
   *
   * 不重置 `#wasActive`——留着它是故意的：提交之后框应该保持隐藏，直到
   * 用户自己拖一个新的出来，而不是每提交一次就自动又冒出一个满画布框
   * （那样会让"Enter 隐藏选框"形同虚设）。只有真正切走再切回裁剪工具，
   * `#ensureDefaultRect` 才会因为 `#wasActive` 被工具切换重置而再给一个。
   */
  commit(): boolean {
    this.#ensureDefaultRect();
    const rect = this.#rect;
    this.#rect = null;
    this.#drag = null;
    this.#drawStart = null;
    if (rect === null || rect.width < 2 || rect.height < 2) return false;
    const done = cropDocument(this.#deps.store, rect);
    this.#deps.requestRender();
    return done;
  }

  /** Esc：丢弃会话，不产生历史。同 commit()，不重置 `#wasActive`——见那边的注释。 */
  cancel(): void {
    this.#rect = null;
    this.#drag = null;
    this.#drawStart = null;
    this.#deps.requestRender();
  }

  /** 文档空间遮罩预览：框外压暗 + 井字参考线 + 8 个手柄。 */
  drawOverlay(ctx: CanvasRenderingContext2D, viewportScale = 1): void {
    this.#ensureDefaultRect(); // 切到裁剪工具后哪怕还没点画布，下一帧就该看见满画布默认框
    const rect = this.#rect;
    const doc = this.#deps.store.document;
    if (rect === null || doc === null) return;
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.beginPath();
    ctx.rect(0, 0, doc.size.width, doc.size.height);
    ctx.rect(rect.x, rect.y, rect.width, rect.height);
    ctx.fill('evenodd');
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 1;
    ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.width - 1, rect.height - 1);

    // 井字参考线（三分法构图辅助）：黑白间隔的虚线，跟选区蚂蚁线
    // （ants-overlay.ts 的 u_dash: 4）同一个节奏——先描黑虚线，再用同样的
    // 虚线间距但整体错开一个虚线长度描白色，两遍叠起来就是黑白交替。
    const dash = 4 / viewportScale;
    ctx.lineWidth = 1 / viewportScale;
    ctx.setLineDash([dash, dash]);
    const drawGridLines = (): void => {
      ctx.beginPath();
      for (const f of [1 / 3, 2 / 3]) {
        const x = rect.x + rect.width * f;
        ctx.moveTo(x, rect.y);
        ctx.lineTo(x, rect.y + rect.height);
        const y = rect.y + rect.height * f;
        ctx.moveTo(rect.x, y);
        ctx.lineTo(rect.x + rect.width, y);
      }
      ctx.stroke();
    };
    ctx.strokeStyle = 'black';
    ctx.lineDashOffset = 0;
    drawGridLines();
    ctx.strokeStyle = 'white';
    ctx.lineDashOffset = dash;
    drawGridLines();
    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;

    // 角/边手柄画成黑色"边角包裹"（跟相机对焦框一样的视觉语言）而不是普通
    // 小方块——让用户一眼看出这四角/四边现在能拖；外面先描一层白边，
    // 免得裁到纯黑图片内容时手柄糊在背景里看不见。
    const arm = 14 / viewportScale;
    ctx.lineCap = 'butt';
    for (const [outerColor, outerWidth] of [
      ['white', 5 / viewportScale],
      ['black', 3 / viewportScale],
    ] as const) {
      ctx.strokeStyle = outerColor;
      ctx.lineWidth = outerWidth;
      for (const handle of CORNER_HANDLES) {
        const p = handlePoint(rect, handle);
        const { dx, dy } = CORNER_INWARD[handle];
        ctx.beginPath();
        ctx.moveTo(p.x, p.y + dy * arm);
        ctx.lineTo(p.x, p.y);
        ctx.lineTo(p.x + dx * arm, p.y);
        ctx.stroke();
      }
      for (const handle of EDGE_HANDLES) {
        const p = handlePoint(rect, handle);
        const horizontal = handle === 'n' || handle === 's';
        ctx.beginPath();
        if (horizontal) {
          ctx.moveTo(p.x - arm, p.y);
          ctx.lineTo(p.x + arm, p.y);
        } else {
          ctx.moveTo(p.x, p.y - arm);
          ctx.lineTo(p.x, p.y + arm);
        }
        ctx.stroke();
      }
    }
    ctx.restore();
  }
}

const CORNER_HANDLES = ['nw', 'ne', 'se', 'sw'] as const;
const EDGE_HANDLES: readonly ScaleHandle[] = ['n', 'e', 's', 'w'];

/** 角手柄的两条臂朝矩形内部延伸的方向（+1/-1）。 */
const CORNER_INWARD: Readonly<Record<'nw' | 'ne' | 'se' | 'sw', { readonly dx: number; readonly dy: number }>> = {
  nw: { dx: 1, dy: 1 },
  ne: { dx: -1, dy: 1 },
  se: { dx: -1, dy: -1 },
  sw: { dx: 1, dy: -1 },
};

function moveRect(rect: Rect, dx: number, dy: number): Rect {
  return { x: rect.x + dx, y: rect.y + dy, width: rect.width, height: rect.height };
}

/** 在 rect 内居中放下一个新比例的最大矩形（"contain"式适配）。 */
function fitAspectWithin(rect: Rect, aspect: number): Rect {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  let width = rect.width;
  let height = width / aspect;
  if (height > rect.height) {
    height = rect.height;
    width = height * aspect;
  }
  return { x: cx - width / 2, y: cy - height / 2, width, height };
}

/** 手柄在哪个轴上有效（角=两轴，边=一轴）——与 handles.ts 的 HANDLE_POSITION 对齐。 */
const HANDLE_AXES: Readonly<Record<ScaleHandle, { readonly x: boolean; readonly y: boolean }>> = {
  nw: { x: true, y: true },
  n: { x: false, y: true },
  ne: { x: true, y: true },
  e: { x: true, y: false },
  se: { x: true, y: true },
  s: { x: false, y: true },
  sw: { x: true, y: true },
  w: { x: true, y: false },
};

/** 拖手柄改变矩形——对边固定，被拖的边跟手；有比例约束时按锁定轴反推另一轴。 */
function resizeRect(
  start: Rect,
  handle: ScaleHandle,
  dx: number,
  dy: number,
  aspect: number | null,
): Rect {
  const axes = HANDLE_AXES[handle];
  const left = handle === 'nw' || handle === 'w' || handle === 'sw';
  const top = handle === 'nw' || handle === 'n' || handle === 'ne';
  const right = start.x + start.width;
  const bottom = start.y + start.height;

  let x = start.x;
  let y = start.y;
  let width = start.width;
  let height = start.height;

  if (axes.x) {
    if (left) {
      x = start.x + dx;
      width = right - x;
    } else {
      width = start.width + dx;
    }
  }
  if (axes.y) {
    if (top) {
      y = start.y + dy;
      height = bottom - y;
    } else {
      height = start.height + dy;
    }
  }

  if (aspect !== null && aspect > 0) {
    if (axes.x && axes.y) {
      // 角手柄：宽是这次拖拽的主导量，高按比例反推（对角锚点不动）
      height = width / aspect;
      if (top) y = bottom - height;
    } else if (axes.x) {
      height = width / aspect;
      y = start.y - (height - start.height) / 2; // 边手柄没有自然锚点，居中展开
    } else if (axes.y) {
      width = height * aspect;
      x = start.x - (width - start.width) / 2;
    }
  }

  return { x, y, width: Math.max(1, width), height: Math.max(1, height) };
}

/** 裁剪框只能在画布范围内——越界的边直接夹到画布边缘（不改变对边）。 */
function clampToCanvas(rect: Rect, canvasWidth: number, canvasHeight: number): Rect {
  const x0 = Math.max(0, Math.min(rect.x, canvasWidth));
  const y0 = Math.max(0, Math.min(rect.y, canvasHeight));
  const x1 = Math.max(0, Math.min(rect.x + rect.width, canvasWidth));
  const y1 = Math.max(0, Math.min(rect.y + rect.height, canvasHeight));
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1),
    width: Math.abs(x1 - x0),
    height: Math.abs(y1 - y0),
  };
}

// 供测试直接验证纯函数（不必搭一整个 CropToolController）
export const __internal = { moveRect, resizeRect, clampToCanvas, fitAspectWithin };
